import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PublicShell } from "./PublicQuote";
import { CheckCircle2, MessageSquareText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicArtwork() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [changes, setChanges] = useState("");
  const [showChanges, setShowChanges] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/artwork/${token}`).then(r => setO(r.data)).catch(() => setErr("Link invalid or expired."));
  }, [token]);

  const approve = async () => {
    try { await axios.post(`${API}/public/artwork/${token}/approve`); setO({ ...o, artwork_status: "approved" }); toast.success("Artwork approved — we're starting production."); }
    catch(e){ toast.error("Failed"); }
  };
  const request = async () => {
    if (!changes.trim()) return toast.error("Tell us what to change");
    try { await axios.post(`${API}/public/artwork/${token}/request-changes`, { notes: changes }); setO({ ...o, artwork_status: "changes_requested" }); toast.success("Changes requested — our team will get back to you."); }
    catch(e){ toast.error("Failed"); }
  };

  if (err) return <PublicShell><p className="text-center p-8">{err}</p></PublicShell>;
  if (!o) return <PublicShell><p className="text-center p-8 text-[#757169]">Loading…</p></PublicShell>;

  const done = o.artwork_status === "approved" || o.artwork_status === "changes_requested";

  return (
    <PublicShell>
      <div className="mb-6">
        <div className="label-tiny mb-1">Artwork approval</div>
        <h1 className="font-display text-3xl tracking-tight">{o.order_no}</h1>
        <div className="text-sm text-[#757169]">For {o.customer_name}</div>
      </div>

      {o.artwork_url ? (
        <div className="card-surface p-4 mb-6">
          <img src={o.artwork_url} alt="Artwork mockup" className="w-full rounded-md" />
        </div>
      ) : null}
      {Array.isArray(o.artwork_urls) && o.artwork_urls.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {o.artwork_urls.map((url, i) => (
            <div key={i} className="card-surface p-3">
              <img src={url} alt={`Mockup ${i+1}`} className="w-full rounded-md" />
              <div className="label-tiny mt-2 text-center">Mockup {i+1}</div>
            </div>
          ))}
        </div>
      )}
      {!o.artwork_url && !(o.artwork_urls || []).length && (
        <div className="card-surface p-8 mb-6 text-center text-[#757169]">
          <div className="label-tiny mb-2">Preview</div>
          <p>Your artwork will appear here shortly.</p>
        </div>
      )}

      {done ? (
        <div className="card-surface p-6 flex items-center gap-3 border-l-4 border-l-[#4A7258]">
          <CheckCircle2 className="text-[#4A7258]" />
          <div>
            <div className="font-display font-semibold">
              {o.artwork_status === "approved" ? "Approved — we're on it." : "Change request received."}
            </div>
            <div className="text-sm text-[#757169]">We'll be in touch shortly.</div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          <button className="btn-primary flex items-center gap-2" onClick={approve} data-testid="artwork-approve"><CheckCircle2 size={16}/> Approve & start production</button>
          <button className="btn-ghost flex items-center gap-2" onClick={()=>setShowChanges(v=>!v)}><MessageSquareText size={16}/> Request changes</button>
        </div>
      )}

      {showChanges && !done && (
        <div className="card-surface p-5 mt-4 fade-up">
          <div className="label-tiny mb-2">What should we change?</div>
          <textarea rows={4} className="input-field mb-3" value={changes} onChange={e=>setChanges(e.target.value)} />
          <div className="flex justify-end"><button className="btn-primary" onClick={request} data-testid="artwork-request-submit">Send</button></div>
        </div>
      )}
    </PublicShell>
  );
}
